/**
 * The revamp's editorial typography, as one definition.
 *
 * Shared by the reader-facing modules and the Business Manager page builder so
 * the editing surface cannot drift from what the visitor sees. The legacy pair
 * works this way already — the editor's Lexical surface and the legacy public
 * render both take `RichTextEditorNodeStyling` — and the revamp broke the
 * pattern by defining its type a second time in Tailwind. These constants put
 * it back.
 *
 * Always scope them to a container: the legacy article must stay untouched.
 */

/**
 * The paragraph component (Figma "Page Builder / Paragraph"): the body in the
 * serif at 22px on a 160% line, tracked in a little, black; a paragraph break
 * of 22px. The design draws the copy on a 730px measure inside its 900px cell,
 * so the paragraphs and lists are capped there while a heading runs the cell.
 *
 * Headings are the same serif at Roman, 32px set at 110%, tracked the same
 * amount, with the design's 35px under them. The serif is the project's stack:
 * the design's own face (TWK Ghost) is not in the kit.
 *
 * `h2` and `h3` are one treatment, not two that happen to match: the Subheading
 * module is declared `h3` and renders `h2`, so moving one without the other
 * would leave the same block set two ways. Move them together.
 */
export const REVAMP_BODY_PROSE = [
    'font-serif font-normal text-black [&_a]:underline [&_li]:leading-[1.6] [&_p]:m-0 [&_p]:mb-[1.4rem] [&_p:last-child]:mb-0',
    'text-[1.125rem] leading-[1.6] tracking-[-0.02em] md:text-[1.375rem] md:leading-[1.6] md:tracking-[-0.03em]',
    'md:[&_ol]:max-w-[45.625rem] md:[&_p]:max-w-[45.625rem] md:[&_ul]:max-w-[45.625rem]',
    '[&_h2]:m-0 [&_h2]:mb-6 [&_h2]:font-serif [&_h2]:font-normal [&_h2]:text-black [&_h3]:m-0 [&_h3]:mb-6 [&_h3]:font-serif [&_h3]:font-normal [&_h3]:text-black',
    '[&_h2]:text-[1.5rem] [&_h2]:leading-[1.1] [&_h2]:tracking-[-0.03em] md:[&_h2]:mb-[2.1875rem] md:[&_h2]:text-[2rem] md:[&_h2]:leading-[1.1] [&_h3]:text-[1.5rem] [&_h3]:leading-[1.1] [&_h3]:tracking-[-0.03em] md:[&_h3]:mb-[2.1875rem] md:[&_h3]:text-[2rem] md:[&_h3]:leading-[1.1]',
    'max-md:[&_h2:not(:first-child)]:mt-10 md:[&_h2:not(:first-child)]:mt-[5.625rem] max-md:[&_h3:not(:first-child)]:mt-10 md:[&_h3:not(:first-child)]:mt-[5.625rem]',
].join(' ');

/**
 * The Subheading block's trailing margin, removed.
 *
 * A heading inside body copy needs the design's 35px under it, and
 * {@link REVAMP_BODY_PROSE} gives every `h2` and `h3` that. A Subheading
 * module is the whole block, so the break to the next module already spans
 * that 35px — left alone the heading adds a second one and the gap doubles.
 *
 * Shared, because the reader and the page builder each render the module and
 * both have to drop it. Held apart from the heading rule rather than folded
 * into it: the rule is right everywhere else it applies.
 */
export const REVAMP_STANDALONE_HEADING = '[&_h2]:mb-0! [&_h3]:mb-0!';

/**
 * The drop cap: the first letter of the lede's first paragraph, set in the
 * paragraph's own serif three lines deep and floated so the copy wraps it. A
 * pseudo-element on the paragraph's own first letter rather than a wrapped
 * span, so the word is read once and copied once.
 *
 * The building blocks are set in two faces — the serif for serif, Neue Haas for
 * sans — so the cap takes the body's face rather than a display one. The
 * masthead and the sections around the blocks keep their own type.
 */
export const REVAMP_DROP_CAP = [
    '[&_p:first-of-type::first-letter]:float-left [&_p:first-of-type::first-letter]:font-serif',
    '[&_p:first-of-type::first-letter]:font-normal [&_p:first-of-type::first-letter]:text-[#1f1f21]',
    '[&_p:first-of-type::first-letter]:text-[4rem] md:[&_p:first-of-type::first-letter]:text-[5rem]',
    '[&_p:first-of-type::first-letter]:mt-[0.2rem] [&_p:first-of-type::first-letter]:leading-[0.8]',
    '[&_p:first-of-type::first-letter]:mr-[0.625rem]',
].join(' ');

/** The quote as every quote component sets it. */
export const REVAMP_QUOTE_PROSE =
    'w-full font-sans font-normal text-black text-[1.75rem] leading-[1.1] tracking-[-0.03em] md:w-full md:max-w-full md:text-[5rem] md:leading-[1.1] md:[&_p]:text-[5rem] md:[&_p]:leading-[1.1] [&_p]:font-sans [&_p]:tracking-[-0.03em] [&_p]:text-black';

/** The name under it: 32px at 130%, at four-fifths ink. */
export const REVAMP_QUOTE_NAME_PROSE =
    'm-0 font-sans font-normal text-black opacity-80 text-[1.25rem] leading-[1.3] tracking-[-0.03em] md:text-[2rem]';

/**
 * The description under a picture (Figma "Description"): 13.9px sans at four
 * fifths ink, on a 400px measure rather than the picture's own width.
 *
 * Shared with the page builder so the caption an author writes is set the way
 * the reader will read it — the legacy builder sets it at the article body's
 * size across the full width of the image, which is a different block entirely.
 */
export const REVAMP_FIGURE_CAPTION =
    'm-0 font-sans text-[0.75rem] leading-[1.3] tracking-[-0.03em] text-black/80 md:w-[25rem] md:text-[0.868rem]';

/** The corner every picture on the revamp carries. */
export const REVAMP_FIGURE_RADIUS = 'rounded-tile';

/**
 * The same corner, for the page builder's picture.
 *
 * The builder's slot nests the image inside the aria `Image` wrapper, and it is
 * that wrapper which clips — so the corner has to be set on both or the picture
 * keeps the legacy 10px. Important, because the legacy shell sets that 10px
 * from a Fela descendant rule that out-specifies a plain utility.
 *
 * Written out rather than composed from the constant above: Tailwind scans
 * source for literal class names, and a class assembled at runtime is never
 * generated.
 */
export const REVAMP_FIGURE_RADIUS_NESTED =
    'rounded-tile! [&_.ui-image]:rounded-tile!';

/**
 * The same corner, set from a container onto every picture beneath it.
 *
 * For the tiled modules — the galleries and the trio — whose slots come from
 * the shared project-builder components rather than the article's own, so there
 * is no per-picture seam to thread it through.
 */
export const REVAMP_FIGURE_RADIUS_DESCENDANTS =
    '[&_.ui-image]:rounded-tile! [&_.ui-image-element]:rounded-tile!';

/**
 * The frame a quote or a slogan stands in (Figma "Page Builder / Quote"): the
 * hairline 129px in from its cell's or the page's edge, the line 25px off it.
 *
 * Shared with the page builder, which otherwise sets the quote's type correctly
 * and then runs it the full width of the page with no rule beside it.
 */
export const REVAMP_QUOTE_FRAME =
    'ml-6 flex flex-col items-start gap-8 border-l border-solid border-black/20 pl-5 md:ml-[8.0625rem] md:gap-[3.875rem] md:pl-[1.5625rem]';

/** The measure the design gives a quote across the page: 1160. */
export const REVAMP_QUOTE_WIDTH_FULL = 'md:w-[74.0625rem]';

/** And in one cell of the grid: 535. */
export const REVAMP_QUOTE_WIDTH_HALF = 'md:w-[35rem]';

/**
 * The gutter the reader's tiled modules stand on — the body row's own 24px,
 * not the builder's 28px. Applied from the container, since the tiles come
 * from the shared project-builder grid.
 */
export const REVAMP_TILE_GRID = [
    // The reader's tiled modules stand on the page's own 48px margins with the
    // body row's 24px between them. The builder's grid caps itself at 1820 and
    // centres, which leaves the row a couple of pixels in from the reader's
    // line and every tile a fraction narrow.
    'md:px-[3rem]',
    '[&>*]:w-full! [&>*]:max-w-none!',
    '[&_[class*=grid]]:gap-6!',
].join(' ');
